/**
 * Application root.
 *
 * Responsibilities — and *only* these:
 *  1. Mount the global providers (React Query, Auth, Demo, Tooltip).
 *  2. Mount the global UI shells that live outside the router (toasts,
 *     PWA prompts, cookie consent).
 *  3. Mount the BrowserRouter and delegate route definitions to
 *     `<AppRoutes />`, which reads from the central registry.
 *  4. Wire the offline-queue sync that runs whenever the browser
 *     comes back online.
 *
 * Anything else — routing, layout, error boundaries, role guards,
 * feature gating — lives in the modules under `@/components` and
 * `@/features`. App.tsx never grows when a new page is added.
 */

import { lazy, Suspense, useEffect, useMemo } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';

import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { PwaInstallPrompt } from '@/components/PwaInstallPrompt';
import { PwaUpdatePrompt } from '@/components/PwaUpdatePrompt';
import { CookieConsent } from '@/components/CookieConsent';
import { AppRoutes } from '@/components/AppRoutes';
import { LeanCleanTourProvider } from '@/components/onboarding/LeanCleanTourProvider';

import { AuthProvider } from '@/contexts/AuthContext';
import { DemoProvider } from '@/contexts/DemoContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { AreaProvider } from '@/contexts/AreaContext';
import { ModeProvider } from '@/contexts/ModeContext';
import { SessionRecordingProvider } from '@/contexts/SessionRecordingContext';
import { AiCompanionProvider } from '@/contexts/AiCompanionContext';
import { SupportModeProvider } from '@/contexts/SupportModeContext';

import { supabase } from '@/integrations/supabase/client';
import { processOfflineQueue } from '@/lib/offlineQueue';
import { createQueryClient } from '@/lib/queryClient';
import { createLogger } from '@/lib/logger';
import { env } from '@/lib/env';

// React Query devtools — dev only, lazy-loaded so they never end up in
// the production bundle. The dynamic import is wrapped in a `Suspense`
// boundary so the dev-only failure mode is "panel never appears" rather
// than "dev mode crashes".
const ReactQueryDevtools = env.isDev
  ? lazy(() =>
      import('@tanstack/react-query-devtools').then((m) => ({ default: m.ReactQueryDevtools })),
    )
  : null;

const log = createLogger('App');

/**
 * Sync any IndexedDB-queued mutations whenever the browser regains
 * connectivity. Runs once on mount as well, in case the queue is
 * non-empty from a previous session.
 */
function useOfflineSync(): void {
  useEffect(() => {
    const handleOnline = async () => {
      const result = await processOfflineQueue(supabase);
      if (result.processed > 0) {
        log.info('offline-queue synced', result);
      }
    };
    window.addEventListener('online', handleOnline);
    if (navigator.onLine) void handleOnline();
    return () => window.removeEventListener('online', handleOnline);
  }, []);
}

const App = () => {
  // One client per app instance — `useMemo` keeps it stable across
  // re-renders without leaking the constructor into module scope.
  const queryClient = useMemo(() => createQueryClient(), []);
  useOfflineSync();

  return (
    // Outermost boundary — keeps the app from white-screening on a
    // crash inside one of the providers.
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
        <DemoProvider>
          <AuthProvider>
            <SupportModeProvider>
            <AiCompanionProvider>
            <SessionRecordingProvider>
            <TooltipProvider>
              <Sonner />
              <PwaInstallPrompt />
              <PwaUpdatePrompt />
              <CookieConsent />
              <BrowserRouter>
                {/*
                  Area- und Mode-Achse: AreaProvider leitet den Bereich
                  (patient/klinik/ambulant/admin) aus Pfad/Demo/Org ab und
                  spiegelt `.area-*` aufs <html>; ModeProvider hält den
                  schlicht/voll-Modus. Beide brauchen Router (useLocation)
                  und Auth/Demo (useFeatures/useDemo), sitzen also hier
                  innen. ModeProvider liegt unter AreaProvider, weil der
                  Default-Modus vom Bereich abhängt.
                */}
                <AreaProvider>
                  <ModeProvider>
                    {/*
                      Phase 5B: the Lean & Clean onboarding tour needs to
                      read `useLocation()` + `useFeatures()`, so it mounts
                      inside the router and inside the auth/demo providers.
                      It never renders anything itself — it's a context
                      shell that drives driver.js imperatively.
                    */}
                    <LeanCleanTourProvider>
                      <AppRoutes />
                    </LeanCleanTourProvider>
                  </ModeProvider>
                </AreaProvider>
              </BrowserRouter>
            </TooltipProvider>
            </SessionRecordingProvider>
            </AiCompanionProvider>
            </SupportModeProvider>
          </AuthProvider>
        </DemoProvider>
        </ThemeProvider>
        {ReactQueryDevtools && (
          <Suspense fallback={null}>
            <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
          </Suspense>
        )}
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
